import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import React from 'react';

import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

/**
 * Skeleton for invoice detail: same div structure as loaded state (header, title row, issued, total, label row, users with items, payins/payouts). No text.
 */
const noop = (): void => {};

export const InvoiceDetailLoading = (): JSX.Element => (
  <PaymentViewLayout
    title=""
    onBack={noop}
    backTitle=""
    secondaryAction={{ title: 'History', onClick: noop, disabled: true }}
    primaryAction={{ title: 'Adjust', onClick: noop, disabled: true }}
  >
    <div className="text-xmd font-helvetica flex flex-row w-full items-start">
      <div className="flex flex-col flex-1 min-w-0">
        {/* Title row */}
        <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
          <div className="flex flex-col h-full min-h-full p-f1 box-border justify-start w-full min-w-f19 border-none" />
          <div className="flex flex-col h-full items-end justify-start p-f1 shrink-0 border-l-[0.5px] border-main-200">
            <div className="w-[60px] h-[24px] rounded-[4px] border-[0.5px] border-main shrink-0 bg-main-200" />
          </div>
        </div>

        {/* Issued row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full min-h-[1lh]">
          <div
            className={clsx(
              'px-f1 py-f1 border-r-[0.5px] border-main-200 shrink-0',
              kLabelColumnWidth
            )}
          />
          <div className="px-f1 py-f1 flex-1 font-helvetica min-h-[1lh]" />
        </div>

        {/* Total row */}
        <div className="flex items-start h-f8 w-full">
          <div
            className={clsx(
              'px-f1 pt-f1 border-r-[0.5px] border-main-200 shrink-0 h-full',
              kLabelColumnWidth
            )}
          />
          <div className="px-f1 pt-f1 flex-1 min-h-[2lh]" />
        </div>

        {/* Label row (Users | Items | Due) */}
        <div className="flex flex-row w-full p-f1 border-t-[0.5px] border-b-[0.5px] border-main-200 min-h-[1lh]">
          <span
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex flex-row flex-1 justify-between min-w-0">
            <span className="border-main-200" />
            <span className="border-main-200" />
          </div>
        </div>

        {/* User 1 */}
        <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 p-f1 border-r-[0.5px] border-main-200 flex items-start min-w-0'
            )}
          >
            <div className="flex items-center gap-x-f1">
              <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
              <span className="flex-1 min-w-0" />
            </div>
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-row justify-end gap-x-f1 p-f1 border-b-[0.5px] border-main-200 min-h-[2lh]" />
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200 border-b-[0.5px]'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 p-f1 min-w-0 overflow-hidden shrink-0 flex-1">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center p-f1 min-w-0 flex-1 overflow-hidden" />
              <div className="flex flex-row justify-end items-center gap-x-f1 p-f1 shrink-0 overflow-hidden min-w-[80px]" />
            </div>
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 p-f1 min-w-0 overflow-hidden shrink-0 flex-1">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center p-f1 min-w-0 flex-1 overflow-hidden" />
              <div className="flex flex-row justify-end items-center gap-x-f1 p-f1 shrink-0 overflow-hidden min-w-[80px]" />
            </div>
          </div>
        </div>

        {/* User 2 */}
        <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 p-f1 border-r-[0.5px] border-main-200 flex items-start min-w-0'
            )}
          >
            <div className="flex items-center gap-x-f1">
              <div className="w-[14px] h-[14px] rounded-full shrink-0 bg-main-200" />
              <span className="flex-1 min-w-0" />
            </div>
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex flex-row justify-end gap-x-f1 p-f1 border-b-[0.5px] border-main-200 min-h-[2lh]" />
            <div
              className={clsx(
                'flex flex-row w-full items-stretch border-main-200 border-b-[0.5px]'
              )}
            >
              <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
              <div className="flex items-center gap-x-f1 p-f1 min-w-0 overflow-hidden shrink-0 flex-1">
                <div className="w-[14px] h-[14px] shrink-0 rounded-[2px] bg-main-200" />
                <span className="flex-1 min-w-0" />
              </div>
              <div className="flex items-center p-f1 min-w-0 flex-1 overflow-hidden" />
              <div className="flex flex-row justify-end items-center gap-x-f1 p-f1 shrink-0 overflow-hidden min-w-[80px]" />
            </div>
          </div>
        </div>

        {/* Payins row */}
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex flex-row justify-between items-center gap-x-f1 p-f1 flex-1 min-w-0">
            <span className="min-w-0" />
            <span className="flex flex-row justify-end gap-x-f1 min-w-0" />
          </div>
        </div>

        {/* Payouts row */}
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 border-r-[0.5px] border-main-200 aspect-square" />
          <div className="flex flex-row justify-between items-center gap-x-f1 p-f1 flex-1 min-w-0">
            <span className="min-w-0" />
            <span className="flex flex-row justify-end gap-x-f1 min-w-0" />
          </div>
        </div>
      </div>
    </div>
  </PaymentViewLayout>
);
